import React from 'react';
import moment from 'moment';
import { Button, Message } from 'semantic-ui-react';
import { Link } from 'react-router-dom';

import TablePaginationMenu from '../../../components/TablePaginationMenu';
import { PropertyWalletHistoryItemDTO } from '../../../services/properties';
import {
  DetailsSection,
  DetailsSectionTitle,
  HistoryItem,
  HistoryItemDate,
  HistoryItemMeta,
  HistoryItemTitle,
  HistoryList
} from '../styles';
import PropertyHistorySkeleton from './PropertyHistorySkeleton';
import {
  formatHistoricEventMeta,
  formatHistoricEventTitle,
  getHistoricCapitalizationLink,
  getHistoricClaimLink,
  getHistoricLenderInsuranceLink,
  getHistoricEventDate
} from '../utils/propertyHistoricFormatters';

const HISTORY_PAGE_SIZE = 10;

type Props = {
  items: PropertyWalletHistoryItemDTO[];
  total: number;
  currentPage: number;
  isLoading: boolean;
  errorMessage: string | null;
  onPageChange: (page: number) => void;
  onRetry: () => void;
  isCondominium?: boolean;
};

const formatEventDate = (item: PropertyWalletHistoryItemDTO): string => {
  const value = getHistoricEventDate(item);
  if (!value) return 'Data não informada';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : value;
};

const PropertyHistoryTimeline: React.FC<Props> = ({
  items,
  total,
  currentPage,
  isLoading,
  errorMessage,
  onPageChange,
  onRetry,
  isCondominium
}) => {
  return (
    <DetailsSection>
      <DetailsSectionTitle>
        Histórico do imóvel
        {!isLoading && !errorMessage
          ? ` (${total} ${total === 1 ? 'evento' : 'eventos'})`
          : ''}
      </DetailsSectionTitle>

      {isLoading && <PropertyHistorySkeleton />}

      {!isLoading && errorMessage && (
        <Message negative>
          <p style={{ margin: '0 0 8px' }}>{errorMessage}</p>
          <Button type="button" size="small" onClick={onRetry}>
            Tentar novamente
          </Button>
        </Message>
      )}

      {!isLoading && !errorMessage && !items.length && (
        <Message info>
          <p style={{ margin: 0 }}>Nenhum evento registrado para este imóvel ainda.</p>
        </Message>
      )}

      {!isLoading && !errorMessage && items.length > 0 && (
        <>
          <HistoryList>
            {items.map((item, index) => {
              const metaLines = formatHistoricEventMeta(item, isCondominium);
              const claimLink = getHistoricClaimLink(item);
              const capitalizationLink = getHistoricCapitalizationLink(item);
              const lenderInsuranceLink = getHistoricLenderInsuranceLink(item);

              return (
                <HistoryItem key={item.publicId || item.id || `${item.code}-${index}`}>
                  <HistoryItemDate>{formatEventDate(item)}</HistoryItemDate>
                  <HistoryItemTitle>{formatHistoricEventTitle(item)}</HistoryItemTitle>
                  {metaLines.length > 0 && (
                    <HistoryItemMeta>{metaLines.join(' · ')}</HistoryItemMeta>
                  )}
                  {claimLink && (
                    <HistoryItemMeta>
                      <Link to={claimLink}>Ver sinistro</Link>
                    </HistoryItemMeta>
                  )}
                  {capitalizationLink && (
                    <HistoryItemMeta>
                      <Link to={capitalizationLink}>Ver capitalização</Link>
                    </HistoryItemMeta>
                  )}
                  {lenderInsuranceLink && (
                    <HistoryItemMeta>
                      <Link to={lenderInsuranceLink}>Ver prestamista</Link>
                    </HistoryItemMeta>
                  )}
                </HistoryItem>
              );
            })}
          </HistoryList>
          {total > HISTORY_PAGE_SIZE && (
            <TablePaginationMenu
              currentPage={currentPage}
              dataCount={total}
              setCurrentPage={onPageChange}
            />
          )}
        </>
      )}
    </DetailsSection>
  );
};

export default PropertyHistoryTimeline;
